import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Modal, Tabs, Button, Typography, Flex, Tag, AntdGrid } from "@murm/ant";
import { CloseOutlined } from "@ant-design/icons";
import { MetricsTab } from "./tabs/MetricsTab";
import { FiltersTab } from "./tabs/FiltersTab";
import { PlacesTab } from "./tabs/PlacesTab";
import { useSelectionManager } from "../../hooks/useSelectionManager";
import { FeatureTypeConfig } from "./config/featureTypes";
import { tabNames, tabDescriptions } from "../../pages/search/config";
import styles from "./DataCatalog.module.css";
import { PENDO_ID, pendoId } from "../../lib/pendoIds";
import { cx } from "../../lib/classNames";

const { Title, Text, Paragraph } = Typography;

const { useBreakpoint } = AntdGrid;

const TAB_KEYS = ["metrics", "filters", "places"];

const DataCatalogModal = ({ isOpen, onClose, onApplySelection, firstActiveTab = "metrics" }) => {
  // Below `md` (768px) the rail no longer leaves room for three cards beside it.
  const screens = useBreakpoint();
  const isPhone = !screens.md;
  const isCompact = !screens.lg;
  const { parameters } = useSelector((state) => state.search);
  const [activeTab, setActiveTab] = useState(firstActiveTab);
  const { selections, addSelection, removeSelection, isSelected } = useSelectionManager(
    parameters,
    isOpen
  );

  useEffect(() => {
    setActiveTab(firstActiveTab);
  }, [firstActiveTab]);

  const handleItemSelect = (item, type) => {
    if (isSelected(item, type)) {
      removeSelection(item, type);
    } else {
      addSelection(item, type);
    }
  };

  const handleApplySelection = () => {
    // `selections.X` only exists once that tab's been touched (useSelectionManager's
    // addSelection) -- otherwise closing after just one metric left the other two undefined.
    const finalSelections = {
      metrics: { metrics: [...(selections.metrics?.metrics || [])] },
      filters: {
        filters: [...(selections.filters?.filters || [])],
        needs_filtering: selections.filters?.needs_filtering,
      },
      places: [...(selections.places || [])],
    };

    const defaultTopline = {
      type: "categorical",
      metric: "topline",
      display: "Registered Voters",
      aggregation: "count",
      value: ["all"],
      display_values: ["All"],
    };

    const hasTopline = finalSelections.metrics.metrics.some(
      (m) => m.metric === "topline" && m.aggregation === "count"
    );

    if (!hasTopline) {
      finalSelections.metrics.metrics.unshift(defaultTopline);
    }

    if (finalSelections.places.length === 0) {
      const defaultPlace = {
        name: "United States",
        geo_query: "",
        geography_pair: "United States",
      };
      finalSelections.places = FeatureTypeConfig.places.handlePlaceSelection(
        finalSelections.places,
        defaultPlace
      );
    }

    console.log("Applying selections:", finalSelections);
    // Optional: a caller without onApplySelection must still close rather than throw.
    onApplySelection?.(finalSelections);
    onClose();
  };

  const handleClose = () => {
    const isEmpty =
      (!selections.metrics?.metrics?.length || selections.metrics.metrics.length === 0) &&
      (!selections.filters?.filters?.length || selections.filters.filters.length === 0) &&
      (!selections.places?.length || selections.places.length === 0);

    // `parameters` is often null (default/reset state, see store/searchPage.js) -- optional
    // chaining here treats that as "every selection is a change" instead of throwing.
    const hasChanges =
      JSON.stringify(selections.metrics) !== JSON.stringify(parameters?.metrics) ||
      JSON.stringify(selections.filters) !== JSON.stringify(parameters?.filters) ||
      JSON.stringify(selections.places) !== JSON.stringify(parameters?.places);

    if (isEmpty || !hasChanges) {
      onClose();
    } else {
      handleApplySelection();
    }
  };

  // Tabs per the Explorer Figma (2a): theme-colored (grape), each with its line of copy above the
  // tab content. Labels and copy come from the search config.
  const tabItems = TAB_KEYS.map((tab) => ({
    key: tab,
    label: <span {...pendoId(PENDO_ID.CATALOG_TAB, tab)}>{tabNames[tab]}</span>,
    children: (
      <div className={styles.tabContent}>
        <Paragraph className={styles.description}>{tabDescriptions[tab]}</Paragraph>
        {tab === "metrics" && (
          <MetricsTab
            activeTab={activeTab}
            onMetricSelect={(item) => handleItemSelect(item, "metrics")}
            isItemSelected={isSelected}
          />
        )}
        {tab === "filters" && (
          <FiltersTab
            activeTab={activeTab}
            onFilterSelect={(item) => handleItemSelect(item, "filters")}
            isItemSelected={isSelected}
          />
        )}
        {tab === "places" && <PlacesTab onSearch={(item) => handleItemSelect(item, "places")} />}
      </div>
    ),
  }));

  const renderTag = (badge, closable = true) => (
    <Tag
      {...pendoId(PENDO_ID.CATALOG_SELECTED_TAG)}
      key={badge.key}
      // Search Guidance "2a. Token States" -> "Data Catalog" (closable) token.
      className={styles.selectedTag}
      closable={closable}
      onClose={(e) => {
        e.preventDefault();
        badge.onClick && badge.onClick();
      }}
    >
      {badge.label}
    </Tag>
  );

  const badgesFor = (type) => {
    if (type === "metrics") {
      return selections.metrics.metrics
        .map((item) => {
          const config = FeatureTypeConfig[item.type];
          if (!config) return null;
          const badges = config.renderBadge(item, (itemToRemove) =>
            removeSelection(itemToRemove, type)
          );
          return Array.isArray(badges) ? badges : [badges];
        })
        .flat()
        .filter(Boolean)
        .map((badge) => renderTag(badge, badge.label !== "Registered Voters"));
    }
    if (type === "filters") {
      return selections.filters.filters
        .map((item) =>
          FeatureTypeConfig.filter
            .renderBadge(item, (itemToRemove) => removeSelection(itemToRemove, type))
            .map((badge) => renderTag(badge))
        )
        .flat();
    }
    return selections.places.map((item) =>
      renderTag(
        FeatureTypeConfig.places.renderBadge(item, (itemToRemove) =>
          removeSelection(itemToRemove, type)
        )
      )
    );
  };

  const hasSelections = (type) =>
    (type === "metrics" && selections.metrics?.metrics.length > 0) ||
    (type === "filters" && selections.filters?.filters.length > 0) ||
    (type === "places" && selections.places?.length > 0);

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width="min(1128px, 96vw)"
      centered
      classNames={{
        content: styles.modalContent,
        body: cx(styles.modalBody, isPhone && styles.modalBodyStacked),
      }}
      closable={false}
      destroyOnHidden
    >
      <div
        className={cx(
          styles.layout,
          isPhone && styles.layoutStacked,
          isCompact && styles.layoutCompact
        )}
      >
        <div className={cx(styles.main, isPhone && styles.mainStacked)}>
          <div className={cx(styles.header, isPhone && styles.stackedStatic)}>
            <Title level={2} className={styles.title}>
              Data Catalog
            </Title>
          </div>
          <div className={cx(styles.tabsArea, isPhone && styles.tabsAreaStacked)}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              destroyOnHidden={false}
              className={cx(styles.tabs, isPhone && styles.tabsStacked)}
            />
          </div>
        </div>

        {/* Right rail (Explorer Figma, 2a): close, selected criteria, Apply. */}
        <div className={cx(styles.rail, isPhone && styles.railStacked)}>
          <div className={styles.railHeader}>
            <Button
              {...pendoId(PENDO_ID.CATALOG_CLOSE)}
              type="text"
              icon={<CloseOutlined />}
              onClick={handleClose}
              aria-label="Close"
              className={styles.railClose}
            />
            <Title level={5} className={styles.railTitle}>
              You&apos;re searching for:
            </Title>
          </div>
          <div className={cx(styles.railBody, isPhone && styles.railBodyStacked)}>
            {TAB_KEYS.filter(hasSelections).map((type) => (
              <div key={type} className={styles.railSection}>
                <Text strong className={styles.railSectionLabel}>
                  {tabNames[type]}
                </Text>
                <Flex wrap="wrap" gap="small">
                  {badgesFor(type)}
                </Flex>
              </div>
            ))}
          </div>
          <div className={styles.railFooter}>
            {/* "Apply" (Figma) — same handler as before: applies when something changed, else closes. */}
            <Button {...pendoId(PENDO_ID.CATALOG_APPLY)} type="primary" onClick={handleClose}>
              Apply
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { DataCatalogModal };
